import type { SluggedId } from "Models";
import toast from "react-hot-toast";
import { cn } from "@/lib/utils";
import { styles } from "../styles";
import { createPortal } from "react-dom";
import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { startOfToday } from "date-fns";
import { Button } from "@/components/ui/button";
import { DatePicker } from "../common/DatePicker";
import { RoleSelector } from "./RoleSelector";
import { ApiError, Field, FieldLabel, fieldLabelCls } from "./fieldPrimitives";
import {
  XIcon,
  SearchIcon,
  ChevronLeftIcon,
  ChevronDownIcon,
  Loader2,
  UserIcon,
  CheckCircle2,
} from "lucide-react";
import {
  useSearchFacilityUsersQuery,
  useAddRosterMemberMutation,
  useCreateRosterMemberMutation,
  useLazySearchLockerUsernameQuery,
} from "../../api/endpoints/roster";
import { LeguesGender, LeguesHandedness, RosterTeeType } from "../../enums";
import { useGetCurrentLocalFacilityId } from "@/modules/facility/facilityHooks";
import type {
  Clubhouse_LeagueV2PhoneSearchUserDto,
  Clubhouse_UserSearchPlayerDto,
} from "../../types/leagueRoster";
import { TeeSwatch } from "../common/TeeBadge";
import { Gender } from "@/types/enums";
import {
  extractServerErrorLines,
  extractServerFieldErrors,
  TEE_TYPE_OPTIONS,
  teeSetIdOf,
} from "../helpers";
import {
  HANDICAP_MIN,
  HANDICAP_MAX,
  validateHandicapField,
} from "./handicapRange";
import { IconAlertCircle } from "@tabler/icons-react";
import { OpenRoundCompletionNotice } from "../common/OpenRoundCompletionNotice";

type View = "search" | "configure" | "new-player";

interface AddPlayerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leagueId: string;
  onAdded?: () => void;
  /** Roster user ids, slugged, as the search results are. */
  existingUserIds?: SluggedId[];
  /** League's home facility; falls back to the current facility when omitted. */
  facilityId?: string;
  /** Men's league tee from Season Setup. Pre-fills men. */
  leagueMensTee?: RosterTeeType | null;
  /** Women's league tee from Season Setup. Pre-fills women. */
  leagueWomensTee?: RosterTeeType | null;
  /**
   * Open with the substitute role already selected. The Substitutes rail has
   * its own Add button, and it should not land the operator on a form set to
   * the role they did not press.
   */
  defaultSubstituteOnly?: boolean;
  /**
   * Why the Player role is unavailable, locking this dialog to substitutes. A
   * finalized team league grows by a whole team (see `AddTeamDialog`), so a
   * full-time add from here would be refused by the server.
   */
  fullTimeAddBlockedReason?: string;
  /**
   * Shown above the commit buttons while the current round is still open.
   * Hidden for a substitute, who is not part of that round's completion.
   */
  openRoundCompletionNotice?: string | null;
}

type ConfigureFields = {
  playerName: string;
  handicapIndex: string;
  teeType: RosterTeeType | "";
};

type NewPlayerFields = {
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  dateOfBirth: string;
  gender: string;
  handedness: string;
  playerName: string;
  handicapIndex: string;
  teeType: RosterTeeType | "";
};

type FieldErrors = Partial<Record<string, string>>;

/**
 * Where a server validation key lands on this form. Keys are the lowercased
 * tail of the field path the API returns in its 400 `ValidationProblemDetails`
 * body, so "User.FirstName" and "firstName" both match. Only fields that render
 * their own message are listed — anything else (gender, tee, …) goes in the
 * banner, where it is at least visible.
 */
const NEW_PLAYER_FIELD_BY_SERVER_KEY: Record<string, keyof NewPlayerFields> = {
  username: "username",
  lockerusername: "username",
  firstname: "firstName",
  lastname: "lastName",
  email: "email",
  emailaddress: "email",
  phonenumber: "phoneNumber",
  phone: "phoneNumber",
  dateofbirth: "dateOfBirth",
  dob: "dateOfBirth",
  handicapindex: "handicapIndex",
  seedhandicap: "handicapIndex",
};

function isFemaleGender(gender?: string | number | null) {
  if (typeof gender === "number") return gender === Gender.FEMALE;
  if (gender == null) return false;
  const value = String(gender).trim().toLowerCase();
  return (
    value === String(LeguesGender.FEMALE).toLowerCase() ||
    value === "female" ||
    value === "woman" ||
    value === "women" ||
    value === "lady" ||
    value === "ladies" ||
    value === "girl" ||
    value === "1"
  );
}

function hasKnownGender(gender?: string | number | null) {
  if (typeof gender === "number") {
    return gender === Gender.MALE || gender === Gender.FEMALE;
  }
  if (gender == null || String(gender).trim() === "") return false;
  if (isFemaleGender(gender)) return true;
  const value = String(gender).trim().toLowerCase();
  return (
    value === "m" ||
    value === "male" ||
    value === "man" ||
    value === "men" ||
    value === "mens" ||
    value === "boy" ||
    value === "0"
  );
}

function startingTee(
  mens: RosterTeeType | null | undefined,
  womens: RosterTeeType | null | undefined,
  gender?: string | number | null,
): RosterTeeType {
  return isFemaleGender(gender)
    ? (womens ?? RosterTeeType.RED)
    : (mens ?? RosterTeeType.WHITE);
}

const TEE_HELPER =
  "Starts at this league's default tee (set at Season Setup) — change it if this player needs something different. Locks once Round 1 has started.";

function identityKey(value?: string | null) {
  const trimmed = value?.trim().toLowerCase();
  return trimmed ? trimmed : undefined;
}

function sameLockerPlayer(
  user: Clubhouse_LeagueV2PhoneSearchUserDto,
  player: Clubhouse_UserSearchPlayerDto,
) {
  const shared = (
    [
      [user.userGuid, player.userGuid],
      [user.username, player.username],
      [user.email, player.email],
    ] as const
  )
    .map(([left, right]) => [identityKey(left), identityKey(right)] as const)
    .filter((pair): pair is [string, string] => !!pair[0] && !!pair[1]);
  if (shared.some(([left, right]) => left === right)) return true;
  // A shared guid, username, or email that disagrees is a different person.
  // Display name is only a fallback when the roster row had none of those.
  if (shared.length > 0) return false;
  const userName = identityKey(user.displayName);
  const playerName = identityKey(player.displayName);
  return !!userName && userName === playerName;
}

/** Player search discards a term of 3 characters or fewer and returns an empty list. */
function profileSearchQuery(user: Clubhouse_LeagueV2PhoneSearchUserDto) {
  return [user.username, user.displayName, user.email]
    .map((value) => value?.trim() ?? "")
    .find((value) => value.length > 3);
}

const DEFAULT_NEW_PLAYER: NewPlayerFields = {
  username: "",
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  dateOfBirth: "",
  gender: String(LeguesGender.MALE),
  handedness: String(LeguesHandedness.RIGHT),
  playerName: "",
  handicapIndex: "",
  teeType: "",
};

/** Prefill Create Player phone only when the search looks like a phone number. */
function phonePrefillFromSearch(query: string): string {
  const trimmed = query.trim();
  if (trimmed.length === 0 || /[A-Za-z]/.test(trimmed)) {
    return "";
  }

  const digits = trimmed.replace(/\D/g, "");
  return digits.length >= 7 ? trimmed : "";
}

const VIEW_TITLES: Record<View, string> = {
  search: "Add Player",
  configure: "Configure Player",
  "new-player": "New Player",
};

export function AddPlayerDialog({
  open,
  onOpenChange,
  leagueId,
  onAdded,
  existingUserIds,
  facilityId: facilityIdProp,
  leagueMensTee = RosterTeeType.WHITE,
  leagueWomensTee = RosterTeeType.RED,
  defaultSubstituteOnly = false,
  fullTimeAddBlockedReason,
  openRoundCompletionNotice,
}: AddPlayerDialogProps) {
  const currentFacilityId = useGetCurrentLocalFacilityId();
  const facilityId = facilityIdProp ?? currentFacilityId;

  const [view, setView] = useState<View>("search");
  const [searchInput, setSearchInput] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [selectedUser, setSelectedUser] =
    useState<Clubhouse_LeagueV2PhoneSearchUserDto | null>(null);
  const [configureFields, setConfigureFields] = useState<ConfigureFields>({
    playerName: "",
    handicapIndex: "",
    teeType: "",
  });
  const [newPlayerFields, setNewPlayerFields] =
    useState<NewPlayerFields>(DEFAULT_NEW_PLAYER);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [usernameValid, setUsernameValid] = useState(false);
  const [substituteOnly, setSubstituteOnly] = useState(defaultSubstituteOnly);
  // True while a profile search is filling in a gender the roster search omitted.
  // Add stays disabled until it settles so the saved tee is the one on screen.
  const [teeLookupPending, setTeeLookupPending] = useState(false);
  // Bumped whenever the operator leaves this player, so a late lookup cannot
  // rewrite the next player's tee.
  const genderLookupGen = useRef(0);
  // Which way the step transition travels: forward on drill-in, back on return.
  const [stepDirection, setStepDirection] = useState<"forward" | "back">(
    "forward",
  );

  const [addRosterMember] = useAddRosterMemberMutation();
  const [createRosterMember] = useCreateRosterMemberMutation();
  const [searchPlayers] = useLazySearchLockerUsernameQuery();

  const { data: searchData, isFetching: isSearching } =
    useSearchFacilityUsersQuery(
      { facilityId: facilityId ?? "", query: submittedQuery },
      { skip: !facilityId || !submittedQuery },
    );

  // The API may return match as a single object or an array depending on the
  // result count — normalise to always be an array before passing to SearchView.
  const rawMatches = searchData?.matches;
  const rawMatch = searchData?.match;
  const searchResults: Clubhouse_LeagueV2PhoneSearchUserDto[] = Array.isArray(
    rawMatches,
  )
    ? rawMatches
    : Array.isArray(rawMatch)
      ? rawMatch
      : rawMatch
        ? [rawMatch]
        : [];

  const hasSearched = !!submittedQuery;

  useEffect(() => {
    if (open) {
      genderLookupGen.current += 1;
      setTeeLookupPending(false);
      setView("search");
      setSearchInput("");
      setSubmittedQuery("");
      setSelectedUser(null);
      setConfigureFields({
        playerName: "",
        handicapIndex: "",
        teeType: startingTee(leagueMensTee, leagueWomensTee),
      });
      setNewPlayerFields(DEFAULT_NEW_PLAYER);
      setErrors({});
      setApiError(null);
      setUsernameValid(false);
      // Back to the role the caller asked for, not a hardcoded "player" — an
      // Add Substitute button that opens a form set to Player is the whole
      // thing `defaultSubstituteOnly` exists to prevent, and this reset runs
      // on open, after the initial state is seeded.
      setSubstituteOnly(defaultSubstituteOnly);
    }
  }, [open, leagueMensTee, leagueWomensTee, defaultSubstituteOnly]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onOpenChange]);

  // The dialog is portaled to <body>, so without this the page behind it keeps
  // its own scrollbar, which reads as a scrollbar on the overlay.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  function handleSearch() {
    const q = searchInput.trim();
    if (q.length < 2) return;
    setSubmittedQuery(q);
  }

  function handleSelectUser(user: Clubhouse_LeagueV2PhoneSearchUserDto) {
    const generation = ++genderLookupGen.current;
    const playerName =
      user.displayName ??
      `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
    setSelectedUser(user);
    setConfigureFields({
      playerName,
      handicapIndex: "",
      teeType: startingTee(leagueMensTee, leagueWomensTee, user.gender),
    });
    setErrors({});
    setApiError(null);
    setStepDirection("forward");
    setView("configure");

    // Facility user search omits gender on older API builds. The player
    // search has carried it for a long time (0 = male, 1 = female), so use
    // that when the roster search did not say.
    if (hasKnownGender(user.gender)) {
      setTeeLookupPending(false);
      return;
    }
    // A short username cannot be searched, so fall through to a longer
    // display name or email. Sending the short term itself returns nobody.
    const query = profileSearchQuery(user);
    if (!query) {
      setTeeLookupPending(false);
      return;
    }
    setTeeLookupPending(true);
    void searchPlayers({ username: query })
      .unwrap()
      .then((result) => {
        if (genderLookupGen.current !== generation) return;
        const players = Array.isArray(result) ? result : [];
        const match = players.find((player) => sameLockerPlayer(user, player));
        if (!match || !hasKnownGender(match.gender)) return;
        const provisional = startingTee(
          leagueMensTee,
          leagueWomensTee,
          user.gender,
        );
        const resolved = startingTee(
          leagueMensTee,
          leagueWomensTee,
          match.gender,
        );
        setConfigureFields((current) =>
          current.teeType === provisional
            ? { ...current, teeType: resolved }
            : current,
        );
      })
      .catch(() => undefined)
      .finally(() => {
        if (genderLookupGen.current === generation) setTeeLookupPending(false);
      });
  }

  function handleGoToNewPlayer() {
    setNewPlayerFields({
      ...DEFAULT_NEW_PLAYER,
      phoneNumber: phonePrefillFromSearch(searchInput),
      teeType: startingTee(
        leagueMensTee,
        leagueWomensTee,
        DEFAULT_NEW_PLAYER.gender,
      ),
    });
    setErrors({});
    setApiError(null);
    setStepDirection("forward");
    setView("new-player");
  }

  function goBack() {
    genderLookupGen.current += 1;
    setTeeLookupPending(false);
    setStepDirection("back");
    setView("search");
    setErrors({});
    setApiError(null);
  }

  /**
   * Add an existing player, without waiting for the server.
   *
   * The roster patches itself optimistically, so the row is on screen the
   * moment this runs — holding the dialog open until the round-trip lands
   * only stops the operator adding the next player, which is usually what
   * they are here to do several times in a row. Client-side validation still
   * runs first and still keeps the dialog open, because that is the one
   * failure they can fix in this form.
   *
   * A server rejection ("already in the league", "max players reached") is
   * not fixable here either way, so it arrives as a toast that names the
   * player: by the time it lands, two more may have been added.
   */
  function handleSaveExisting() {
    if (!selectedUser?.userId || teeLookupPending) return;
    const handicapError = validateHandicapField(configureFields.handicapIndex);
    if (handicapError) {
      setErrors({ handicapIndex: handicapError });
      return;
    }
    setErrors({});
    setApiError(null);
    const addedName =
      configureFields.playerName.trim() ||
      selectedUser.displayName ||
      "That player";
    onAdded?.();
    onOpenChange(false);
    void (async () => {
      try {
        await addRosterMember({
          facilityId: facilityId ?? "",
          leagueId,
          body: {
            userId: selectedUser.userId,
            playerName: configureFields.playerName.trim() || undefined,
            // An aG handicap is the player's established, system-computed index,
            // so it is sent as the member's currentHandicap. The typed field is
            // only offered to a player who has none, and seeds them instead.
            // `??` not `||`: a scratch golfer's 0 is a real handicap.
            currentHandicap: selectedUser.agHandicap ?? undefined,
            seedHandicap:
              selectedUser.agHandicap == null && configureFields.handicapIndex
                ? Number(configureFields.handicapIndex)
                : undefined,
            // Send the tee the operator was actually shown: the dropdown starts at
            // the league tee for this player's gender. Omitting it would leave
            // the member Unassigned, or let a profile tee win over the league.
            //
            // Converted to the numeric id the write side takes; the UI works in
            // the name domain the roster reads back. `?? undefined` because "not
            // chosen" omits the key — a clear is only expressible on edit, where
            // the member already exists.
            teeSetId: teeSetIdOf(configureFields.teeType || null) ?? undefined,
            isSubstituteOnly: substituteOnly || undefined,
          },
        }).unwrap();
      } catch (err: unknown) {
        const status = (err as { status?: number })?.status;
        // The server's own message where there is one (e.g. "Max players
        // reached"); the friendly 409 wording otherwise.
        const serverMessage = extractServerErrorLines(err).join("\n");
        toast.error(
          serverMessage ||
            (status === 409
              ? addedName + " is already in this league."
              : "Couldn't add " + addedName + ". Please try again."),
        );
      }
    })();
  }

  // Editing a field drops the message the server left on it, so a corrected
  // username can go back to showing its availability check.
  function clearNewPlayerError(key: keyof NewPlayerFields) {
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  }

  async function handleSaveNewPlayer() {
    const next: FieldErrors = {};
    if (!newPlayerFields.username.trim())
      next.username = "Username is required";
    else if (!usernameValid)
      next.username = "Check username availability before saving";
    if (!newPlayerFields.firstName.trim())
      next.firstName = "First name is required";
    if (!newPlayerFields.lastName.trim())
      next.lastName = "Last name is required";
    if (!newPlayerFields.email.trim()) next.email = "Email is required";
    if (!newPlayerFields.phoneNumber.trim())
      next.phoneNumber = "Phone number is required";
    if (!newPlayerFields.dateOfBirth)
      next.dateOfBirth = "Date of birth is required";
    const handicapError = validateHandicapField(newPlayerFields.handicapIndex);
    if (handicapError) next.handicapIndex = handicapError;
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});
    setApiError(null);
    setIsSaving(true);
    try {
      await createRosterMember({
        facilityId: facilityId ?? "",
        leagueId,
        body: {
          username: newPlayerFields.username.trim(),
          email: newPlayerFields.email.trim(),
          phoneNumber: newPlayerFields.phoneNumber.trim(),
          firstName: newPlayerFields.firstName.trim(),
          lastName: newPlayerFields.lastName.trim(),
          dateOfBirth: newPlayerFields.dateOfBirth,
          gender: newPlayerFields.gender as LeguesGender,
          handedness: newPlayerFields.handedness as LeguesHandedness,
          playerName: newPlayerFields.playerName.trim() || undefined,
          seedHandicap: newPlayerFields.handicapIndex
            ? Number(newPlayerFields.handicapIndex)
            : undefined,
          handicapIndex: newPlayerFields.handicapIndex
            ? Number(newPlayerFields.handicapIndex)
            : undefined,
          teeSetId: teeSetIdOf(newPlayerFields.teeType || null) ?? undefined,
          isSubstituteOnly: substituteOnly || undefined,
        },
      }).unwrap();
      onAdded?.();
      onOpenChange(false);
    } catch (err: unknown) {
      // A 400 comes back as ValidationProblemDetails: put each field's message
      // on the field it belongs to, and keep only what has nowhere to go in the
      // banner, so the operator sees what to fix rather than "try again".
      const fieldErrors = extractServerFieldErrors(err);
      const inline: FieldErrors = {};
      const unattached: string[] = [];
      Object.entries(fieldErrors).forEach(([key, message]) => {
        const field = NEW_PLAYER_FIELD_BY_SERVER_KEY[key];
        if (!field) {
          unattached.push(message);
          return;
        }
        // Several server keys share one field (the body sends both
        // `seedHandicap` and `handicapIndex`, so one bad value can be rejected
        // twice), and dropping the second message would lose a reason.
        const existing = inline[field];
        inline[field] = existing ? existing + "\n" + message : message;
      });
      setErrors(inline);
      // Field messages sit on the fields, so the banner's job is to say that
      // there are some — a form this long can put one below the fold.
      const hint = Object.keys(inline).length
        ? "Please fix the highlighted fields."
        : "";
      const banner =
        [hint, ...unattached].filter(Boolean).join("\n") ||
        extractServerErrorLines(err).join("\n");
      setApiError(banner || "Failed to create player. Please try again.");
    } finally {
      setIsSaving(false);
    }
  }

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[200] grid place-items-center overflow-hidden">
      <button
        type="button"
        aria-label="Dismiss dialog"
        className="absolute inset-0 bg-black/30 backdrop-blur-sm motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-200"
        onClick={() => onOpenChange(false)}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={VIEW_TITLES[view]}
        className="relative z-10 flex flex-col w-full max-w-lg max-h-[90vh] rounded-2xl bg-popover text-sm text-popover-foreground shadow-xl motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-95 motion-safe:slide-in-from-bottom-2 motion-safe:duration-200"
      >
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="absolute top-2 right-2 inline-flex items-center justify-center size-9 rounded-full bg-primary/6 text-primary hover:bg-primary/12 transition-colors"
        >
          <XIcon className="size-4" />
          <span className="sr-only">Close</span>
        </button>

        <div className="flex shrink-0 items-center gap-2 px-6 pt-6 pb-4 pr-14">
          {view !== "search" && (
            <button
              type="button"
              onClick={goBack}
              className="inline-flex items-center justify-center size-7 rounded-full text-text-muted hover:text-text-primary hover:bg-surface transition-colors shrink-0"
            >
              <ChevronLeftIcon className="size-4" />
              <span className="sr-only">Back</span>
            </button>
          )}
          <h2 className="poppins text-xl font-bold leading-none tracking-tight">
            {VIEW_TITLES[view]}
          </h2>
        </div>

        <div className="themed-scrollbar min-h-0 flex-1 overflow-y-auto px-6 pb-6">
          <div
            key={view}
            className={cn(
              "motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-200 motion-safe:ease-out",
              stepDirection === "forward"
                ? "motion-safe:slide-in-from-right-3"
                : "motion-safe:slide-in-from-left-3",
            )}
          >
            {view === "search" && (
              <SearchView
                searchInput={searchInput}
                setSearchInput={setSearchInput}
                onSearch={handleSearch}
                isSearching={isSearching}
                hasSearched={hasSearched}
                submittedQuery={submittedQuery}
                searchResults={searchResults}
                existingUserIds={existingUserIds}
                onSelectUser={handleSelectUser}
                onAddNew={handleGoToNewPlayer}
              />
            )}

            {view === "configure" && selectedUser && (
              <ConfigureView
                user={selectedUser}
                fields={configureFields}
                setFields={setConfigureFields}
                errors={errors}
                apiError={apiError}
                substituteOnly={substituteOnly}
                onSubstituteOnlyChange={setSubstituteOnly}
                roleLockedReason={fullTimeAddBlockedReason}
                leagueMensTee={leagueMensTee}
                leagueWomensTee={leagueWomensTee}
              />
            )}

            {view === "new-player" && (
              <NewPlayerView
                fields={newPlayerFields}
                setFields={setNewPlayerFields}
                errors={errors}
                onClearError={clearNewPlayerError}
                apiError={apiError}
                facilityId={facilityId ?? ""}
                onUsernameStatusChange={setUsernameValid}
                substituteOnly={substituteOnly}
                onSubstituteOnlyChange={setSubstituteOnly}
                roleLockedReason={fullTimeAddBlockedReason}
                leagueMensTee={leagueMensTee}
                leagueWomensTee={leagueWomensTee}
              />
            )}
          </div>
        </div>

        <div className="flex shrink-0 flex-col gap-3 border-t border-border px-6 py-4">
          {!substituteOnly && (
            <OpenRoundCompletionNotice message={openRoundCompletionNotice} />
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="font-bold text-primary rounded-md"
            >
              Cancel
            </Button>
            {view === "configure" && (
              <Button
                type="button"
                onClick={() => void handleSaveExisting()}
                disabled={isSaving || teeLookupPending}
                className="bg-primary font-bold text-white rounded-md"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  "Add to League"
                )}
              </Button>
            )}
            {view === "new-player" && (
              <Button
                type="button"
                onClick={() => void handleSaveNewPlayer()}
                disabled={isSaving}
                className="bg-primary font-bold text-white rounded-md"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  "Create & Add"
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Search view ─────────────────────────────────────────────────────────────

interface SearchViewProps {
  searchInput: string;
  setSearchInput: (v: string) => void;
  onSearch: () => void;
  isSearching: boolean;
  hasSearched: boolean;
  submittedQuery: string;
  searchResults: Clubhouse_LeagueV2PhoneSearchUserDto[];
  /** Roster user ids, slugged, as the search results are. */
  existingUserIds?: SluggedId[];
  onSelectUser: (user: Clubhouse_LeagueV2PhoneSearchUserDto) => void;
  onAddNew: () => void;
}

function SearchView({
  searchInput,
  setSearchInput,
  onSearch,
  isSearching,
  hasSearched,
  submittedQuery,
  searchResults,
  existingUserIds,
  onSelectUser,
  onAddNew,
}: SearchViewProps) {
  // Show every match, including players already on the roster. Those are
  // rendered disabled and tagged "Already in league" rather than filtered out,
  // which read as "no matches found" for a player who plainly does exist.
  // Both sides are slugged, and nothing coerces: a slug cannot be made into a
  // number, and forcing one would match nothing while looking deliberate. This
  // tag is all that stops an operator re-adding someone already on the roster.
  const isExistingMember = (u: Clubhouse_LeagueV2PhoneSearchUserDto) =>
    u.userId != null && (existingUserIds ?? []).includes(u.userId);

  return (
    <div className="flex flex-col gap-4">
      {/* The search row and its escape hatch are one group (8px); the results
          below are a separate group, separated by the stack's 16px. */}
      <div className="flex flex-col gap-2">
        <div className="flex gap-2 pt-2">
          {/* The icon sits in a box pinned to the input's own height (h-9, from
            wrapper top) and centers inside it. Centering on the wrapper instead
            drifts off whenever the wrapper is taller than the input — the row
            stretches it to the Search button's height, and a plain block adds
            line-box space under an inline input. */}
          <div className="relative flex flex-1 items-start">
            <span className="pointer-events-none absolute left-2.5 top-0 flex h-9 items-center">
              <SearchIcon className="size-3.5 text-text-muted" />
            </span>
            <input
              type="text"
              className={cn(styles.wizardInputCls, "pl-8")}
              placeholder="Search by name, username, or phone"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSearch();
              }}
            />
          </div>
          <Button
            type="button"
            onClick={onSearch}
            disabled={isSearching || searchInput.trim().length < 2}
            className="bg-primary font-bold text-white rounded-md shrink-0"
          >
            {isSearching ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              "Search"
            )}
          </Button>
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={onAddNew}
            className="inline-flex items-center gap-1 rounded-sm text-xs font-medium text-primary transition-colors hover:text-primary/80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            + Create new player
          </button>
        </div>
      </div>

      {hasSearched && !isSearching && (
        <>
          {searchResults.length > 0 ? (
            <div className="flex flex-col gap-2">
              <p className={cn(fieldLabelCls, "text-text-muted")}>
                {searchResults.length} result
                {searchResults.length !== 1 ? "s" : ""} for &ldquo;
                {submittedQuery}&rdquo;
              </p>
              <div className="rounded-md border border-border overflow-hidden">
                {searchResults.map((user) => {
                  const already = isExistingMember(user);
                  return (
                    <button
                      key={user.userId ?? 0}
                      type="button"
                      disabled={already}
                      onClick={() => onSelectUser(user)}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-3 text-left transition-colors border-b border-border last:border-0",
                        already
                          ? "opacity-60 cursor-not-allowed"
                          : "hover:bg-surface/60",
                      )}
                    >
                      <div className="size-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <UserIcon className="size-4 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-text-primary poppins truncate">
                          {(user.displayName ??
                            `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim()) ||
                            "Unknown"}
                        </p>
                        <p className="text-xs text-text-muted truncate">
                          {[user.username, user.email, user.phoneDigits]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                      </div>
                      {already && (
                        <span className="poppins shrink-0 whitespace-nowrap rounded-full border border-border px-2 py-0.5 text-xs font-medium tracking-tight text-text-muted">
                          Already in league
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={onAddNew}
                className="w-fit rounded-sm text-xs font-medium text-primary transition-colors hover:text-primary/80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                Not listed? + Create a new player
              </button>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-border bg-surface/50 px-4 py-6 text-center flex flex-col items-center gap-3">
              <p className="text-sm text-text-muted">
                No players found for &ldquo;{submittedQuery}&rdquo;
              </p>
              <button
                type="button"
                onClick={onAddNew}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary bg-primary/8 hover:bg-primary/14 px-3 py-1.5 rounded-full transition-colors"
              >
                + Create new player
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Configure view ───────────────────────────────────────────────────────────

function LeagueTeeReference({
  label,
  tee,
}: {
  label: string;
  tee: RosterTeeType;
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span>{label}</span>
      <TeeSwatch teeType={tee} className="text-sm text-text-primary" />
    </span>
  );
}

/** Optional tee-preference dropdown (shared by both views). */
function TeeSelect({
  id,
  value,
  onChange,
  leagueMensTee,
  leagueWomensTee,
}: {
  id: string;
  value: RosterTeeType | "";
  onChange: (v: RosterTeeType | "") => void;
  leagueMensTee?: RosterTeeType | null;
  leagueWomensTee?: RosterTeeType | null;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const mensTee = leagueMensTee ?? RosterTeeType.WHITE;
  const womensTee = leagueWomensTee ?? RosterTeeType.RED;
  const listId = `${id}-list`;
  const selectedIndex = Math.max(
    0,
    TEE_TYPE_OPTIONS.findIndex((tee) => tee === value),
  );

  function openAt(index: number) {
    setActiveIndex(index);
    setMenuOpen(true);
  }

  function choose(index: number) {
    const tee = TEE_TYPE_OPTIONS[index];
    if (!tee) return;
    onChange(tee);
    setMenuOpen(false);
  }

  function onKeyDown(event: ReactKeyboardEvent<HTMLButtonElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!menuOpen) openAt(selectedIndex);
      else
        setActiveIndex((index) =>
          Math.min(TEE_TYPE_OPTIONS.length - 1, index + 1),
        );
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!menuOpen) openAt(selectedIndex);
      else setActiveIndex((index) => Math.max(0, index - 1));
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      openAt(0);
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      openAt(TEE_TYPE_OPTIONS.length - 1);
      return;
    }
    if ((event.key === "Enter" || event.key === " ") && menuOpen) {
      event.preventDefault();
      choose(activeIndex);
      return;
    }
    if (event.key === "Escape" && menuOpen) {
      event.preventDefault();
      event.stopPropagation();
      setMenuOpen(false);
    }
  }

  useEffect(() => {
    if (!menuOpen) return;
    function onPointerDown(event: MouseEvent) {
      const root = rootRef.current;
      if (
        root &&
        event.target instanceof Node &&
        !root.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const tee = TEE_TYPE_OPTIONS[activeIndex];
    if (!tee) return;
    const option = document.getElementById(`${id}-option-${tee}`);
    if (typeof option?.scrollIntoView === "function") {
      option.scrollIntoView({ block: "nearest" });
    }
  }, [menuOpen, activeIndex, id]);

  return (
    <Field hint={TEE_HELPER}>
      <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-text-muted">
        <span>League default:</span>
        <LeagueTeeReference label="Men's tee" tee={mensTee} />
        <LeagueTeeReference label="Women's tee" tee={womensTee} />
      </p>
      <FieldLabel htmlFor={id}>Tee Preference</FieldLabel>
      <div className="relative" ref={rootRef}>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={menuOpen}
          aria-controls={listId}
          aria-activedescendant={
            menuOpen
              ? `${id}-option-${TEE_TYPE_OPTIONS[activeIndex]}`
              : undefined
          }
          className={cn(
            styles.wizardInputCls,
            "flex items-center justify-between gap-2 text-left",
          )}
          onClick={() => {
            if (menuOpen) setMenuOpen(false);
            else openAt(selectedIndex);
          }}
          onKeyDown={onKeyDown}
        >
          {value ? (
            <TeeSwatch teeType={value} className="text-sm text-text-primary" />
          ) : (
            <span className="text-text-muted">Select a tee</span>
          )}
          <ChevronDownIcon className="size-4 shrink-0 text-text-muted" />
        </button>
        {menuOpen && (
          <ul
            id={listId}
            role="listbox"
            aria-label="Tee Preference"
            className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md border border-border bg-popover py-1 shadow-lg"
          >
            {TEE_TYPE_OPTIONS.map((tee, index) => (
              <li key={tee} role="presentation">
                <button
                  id={`${id}-option-${tee}`}
                  type="button"
                  role="option"
                  aria-selected={value === tee}
                  className={cn(
                    "flex w-full items-center px-3 py-1.5 text-left hover:bg-muted",
                    index === activeIndex && "bg-muted",
                  )}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => choose(index)}
                >
                  <TeeSwatch
                    teeType={tee}
                    className="text-sm text-text-primary"
                  />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Field>
  );
}

interface ConfigureViewProps {
  user: Clubhouse_LeagueV2PhoneSearchUserDto;
  fields: ConfigureFields;
  setFields: (f: ConfigureFields) => void;
  errors: FieldErrors;
  apiError: string | null;
  substituteOnly: boolean;
  onSubstituteOnlyChange: (v: boolean) => void;
  roleLockedReason?: string;
  leagueMensTee?: RosterTeeType | null;
  leagueWomensTee?: RosterTeeType | null;
}

function ConfigureView({
  user,
  fields,
  setFields,
  errors,
  apiError,
  substituteOnly,
  onSubstituteOnlyChange,
  roleLockedReason,
  leagueMensTee,
  leagueWomensTee,
}: ConfigureViewProps) {
  const displayName =
    (user.displayName ??
      `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim()) ||
    "Unknown";

  // A tee that comes back with the search result is the player's own account
  // setting: show it, but do not let the operator override it here. Null means
  // no account tee, which is what falls back to the editable TeeSelect.
  // `?? null`, not truthiness — a scratch golfer's handicap is 0.
  const agHandicap = user.agHandicap ?? null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3 rounded-xl border border-border bg-surface/50 px-4 py-3">
        <div className="size-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <UserIcon className="size-4 text-primary" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-text-primary poppins">
            {displayName}
          </p>
          {user.email && (
            <p className="text-xs text-text-muted truncate">{user.email}</p>
          )}
        </div>
      </div>

      <Field hint="Override how this player's name appears in the league. Defaults to their account name.">
        <FieldLabel htmlFor="configure-playerName" optional>
          League Display Name
        </FieldLabel>
        <input
          id="configure-playerName"
          type="text"
          className={styles.wizardInputCls}
          placeholder={displayName}
          value={fields.playerName}
          onChange={(e) => setFields({ ...fields, playerName: e.target.value })}
        />
      </Field>

      {agHandicap !== null ? (
        <Field hint="This player's established aG handicap. It becomes their current handicap in this league.">
          <p id="configure-handicap-label" className={fieldLabelCls}>
            aG Handicap
          </p>
          <output
            id="configure-handicap"
            aria-labelledby="configure-handicap-label"
            className="monoNumber text-2xl font-bold leading-none tabular-nums text-text-primary"
          >
            {agHandicap.toFixed(1)}
          </output>
        </Field>
      ) : (
        <Field error={errors.handicapIndex}>
          <FieldLabel htmlFor="configure-handicap" optional>
            Starting Handicap
          </FieldLabel>
          <input
            id="configure-handicap"
            type="number"
            min={HANDICAP_MIN}
            max={HANDICAP_MAX}
            step={0.1}
            className={styles.wizardInputCls}
            placeholder="e.g. 12.4"
            value={fields.handicapIndex}
            aria-invalid={!!errors.handicapIndex || undefined}
            onChange={(e) =>
              setFields({ ...fields, handicapIndex: e.target.value })
            }
          />
        </Field>
      )}

      <TeeSelect
        id="configure-tee"
        value={fields.teeType}
        onChange={(v) => setFields({ ...fields, teeType: v })}
        leagueMensTee={leagueMensTee}
        leagueWomensTee={leagueWomensTee}
      />

      <RoleSelector
        substituteOnly={substituteOnly}
        onChange={onSubstituteOnlyChange}
        lockedReason={roleLockedReason}
      />

      {apiError && <ApiError message={apiError} />}
    </div>
  );
}

// ─── New player view ──────────────────────────────────────────────────────────

type UsernameCheckStatus = "idle" | "checking" | "available" | "taken";

interface NewPlayerViewProps {
  fields: NewPlayerFields;
  setFields: (f: NewPlayerFields) => void;
  errors: FieldErrors;
  onClearError: (key: keyof NewPlayerFields) => void;
  apiError: string | null;
  facilityId: string;
  onUsernameStatusChange: (valid: boolean) => void;
  substituteOnly: boolean;
  onSubstituteOnlyChange: (v: boolean) => void;
  roleLockedReason?: string;
  leagueMensTee?: RosterTeeType | null;
  leagueWomensTee?: RosterTeeType | null;
}

function NewPlayerView({
  fields,
  setFields,
  errors,
  onClearError,
  apiError,
  onUsernameStatusChange,
  substituteOnly,
  onSubstituteOnlyChange,
  roleLockedReason,
  leagueMensTee = RosterTeeType.WHITE,
  leagueWomensTee = RosterTeeType.RED,
}: NewPlayerViewProps) {
  const [usernameCheckStatus, setUsernameCheckStatus] =
    useState<UsernameCheckStatus>("idle");
  const [triggerUsernameCheck] = useLazySearchLockerUsernameQuery();

  // One line under the username, chosen by how much it actually tells the
  // operator. A concluded "taken" outranks the form's own error, because
  // failing to save with an unavailable username *sets* that error ("check
  // availability before saving") and it would otherwise bury the specific
  // answer the check already gave.
  const usernameMessage: {
    tone: "error" | "success" | "pending";
    text: string;
  } | null =
    usernameCheckStatus === "taken"
      ? { tone: "error", text: "Username already taken" }
      : errors.username
        ? { tone: "error", text: errors.username }
        : usernameCheckStatus === "checking"
          ? { tone: "pending", text: "Checking availability…" }
          : usernameCheckStatus === "available"
            ? { tone: "success", text: "Username available" }
            : null;

  function set(key: keyof NewPlayerFields, value: string) {
    setFields({ ...fields, [key]: value });
    onClearError(key);
    if (key === "username") {
      setUsernameCheckStatus("idle");
      onUsernameStatusChange(false);
    }
  }

  async function handleUsernameBlur() {
    const username = fields.username.trim();
    if (!username) return;
    setUsernameCheckStatus("checking");
    try {
      const result = await triggerUsernameCheck({ username }).unwrap();
      if (result.some((r) => r.username === username)) {
        setUsernameCheckStatus("taken");
        onUsernameStatusChange(false);
      } else {
        setUsernameCheckStatus("available");
        onUsernameStatusChange(true);
      }
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      if (status === 404) {
        setUsernameCheckStatus("available");
        onUsernameStatusChange(true);
      } else {
        setUsernameCheckStatus("idle");
        onUsernameStatusChange(false);
      }
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-4">
        <Field error={errors.firstName}>
          <FieldLabel htmlFor="new-firstName" required>
            First Name
          </FieldLabel>
          <input
            id="new-firstName"
            type="text"
            className={styles.wizardInputCls}
            value={fields.firstName}
            aria-invalid={!!errors.firstName || undefined}
            onChange={(e) => set("firstName", e.target.value)}
          />
        </Field>
        <Field error={errors.lastName}>
          <FieldLabel htmlFor="new-lastName" required>
            Last Name
          </FieldLabel>
          <input
            id="new-lastName"
            type="text"
            className={styles.wizardInputCls}
            value={fields.lastName}
            aria-invalid={!!errors.lastName || undefined}
            onChange={(e) => set("lastName", e.target.value)}
          />
        </Field>
      </div>

      <Field error={errors.email}>
        <FieldLabel htmlFor="new-email" required>
          Email
        </FieldLabel>
        <input
          id="new-email"
          type="email"
          className={styles.wizardInputCls}
          value={fields.email}
          aria-invalid={!!errors.email || undefined}
          onChange={(e) => set("email", e.target.value)}
        />
      </Field>

      <Field
        status={
          // A real element in the tree at all times, so the result is announced
          // when it lands — focus has already left the input by then.
          <span aria-live="polite" className="empty:hidden">
            {usernameMessage && (
              <span
                className={cn(
                  "flex items-center gap-1 text-xs",
                  usernameMessage.tone === "error" && "text-destructive",
                  usernameMessage.tone === "success" &&
                    "text-green-600 dark:text-green-400",
                  usernameMessage.tone === "pending" && "text-text-muted",
                )}
              >
                {usernameMessage.tone === "pending" ? (
                  <Loader2 className="size-3 shrink-0 animate-spin" />
                ) : usernameMessage.tone === "success" ? (
                  <CheckCircle2 className="size-3 shrink-0 motion-safe:animate-in motion-safe:zoom-in-50 motion-safe:duration-200" />
                ) : (
                  <IconAlertCircle className="size-3 shrink-0" />
                )}
                {usernameMessage.text}
              </span>
            )}
          </span>
        }
      >
        <FieldLabel htmlFor="new-username" required>
          Username
        </FieldLabel>
        <input
          id="new-username"
          type="text"
          autoComplete="off"
          className={styles.wizardInputCls}
          value={fields.username}
          onChange={(e) => set("username", e.target.value)}
          onBlur={() => {
            void handleUsernameBlur();
          }}
        />
      </Field>

      <Field error={errors.phoneNumber}>
        <FieldLabel htmlFor="new-phoneNumber" required>
          Phone Number
        </FieldLabel>
        <input
          id="new-phoneNumber"
          type="tel"
          className={styles.wizardInputCls}
          value={fields.phoneNumber}
          aria-invalid={!!errors.phoneNumber || undefined}
          onChange={(e) => set("phoneNumber", e.target.value)}
        />
      </Field>

      <Field error={errors.dateOfBirth}>
        <FieldLabel htmlFor="new-dob" required>
          Date of Birth
        </FieldLabel>
        <DatePicker
          id="new-dob"
          value={fields.dateOfBirth}
          onChange={(v) => set("dateOfBirth", v)}
          maxDate={startOfToday()}
          captionLayout="dropdown"
          placeholder="Select date of birth"
          aria-invalid={!!errors.dateOfBirth}
          className="rounded-md"
        />
      </Field>

      <div className="grid grid-cols-2 gap-4">
        <Field>
          <FieldLabel htmlFor="new-gender" required>
            Gender
          </FieldLabel>
          <select
            id="new-gender"
            className={styles.wizardInputCls}
            value={fields.gender}
            onChange={(e) => {
              const gender = e.target.value;
              const stillOnGenderDefault =
                fields.teeType ===
                startingTee(leagueMensTee, leagueWomensTee, fields.gender);
              setFields({
                ...fields,
                gender,
                teeType: stillOnGenderDefault
                  ? startingTee(leagueMensTee, leagueWomensTee, gender)
                  : fields.teeType,
              });
            }}
          >
            <option value="M">Male</option>
            <option value="F">Female</option>
          </select>
        </Field>
        <Field>
          <FieldLabel htmlFor="new-handedness" required>
            Handedness
          </FieldLabel>
          <select
            id="new-handedness"
            className={styles.wizardInputCls}
            value={fields.handedness}
            onChange={(e) => set("handedness", e.target.value)}
          >
            <option value={String(LeguesHandedness.RIGHT)}>Right</option>
            <option value={String(LeguesHandedness.LEFT)}>Left</option>
          </select>
        </Field>
      </div>

      <div className="border-t border-border pt-6 flex flex-col gap-4">
        <h3 className="poppins text-sm font-semibold tracking-tight text-text-primary">
          League settings
          <span className="ml-1 font-normal text-text-muted">(optional)</span>
        </h3>
        <Field>
          <FieldLabel htmlFor="new-playerName">League Display Name</FieldLabel>
          <input
            id="new-playerName"
            type="text"
            className={styles.wizardInputCls}
            placeholder="Defaults to first + last name"
            value={fields.playerName}
            onChange={(e) => set("playerName", e.target.value)}
          />
        </Field>
        <Field error={errors.handicapIndex}>
          <FieldLabel htmlFor="new-handicap">Starting Handicap</FieldLabel>
          <input
            id="new-handicap"
            type="number"
            min={HANDICAP_MIN}
            max={HANDICAP_MAX}
            step={0.1}
            className={styles.wizardInputCls}
            placeholder="e.g. 12.4"
            value={fields.handicapIndex}
            aria-invalid={!!errors.handicapIndex || undefined}
            onChange={(e) => set("handicapIndex", e.target.value)}
          />
        </Field>
        <TeeSelect
          id="new-tee"
          value={fields.teeType}
          onChange={(v) => set("teeType", v)}
          leagueMensTee={leagueMensTee}
          leagueWomensTee={leagueWomensTee}
        />
        <RoleSelector
          substituteOnly={substituteOnly}
          onChange={onSubstituteOnlyChange}
          lockedReason={roleLockedReason}
        />
      </div>

      {apiError && <ApiError message={apiError} />}
    </div>
  );
}
